<template>
	<div class="p4">
		<!-- Render fields -->
		<div v-if="!isLoading">
			<div v-for="(field, i) in computedSchema" :key="i" class="mb4">
				<form-field
					:name="field.entity + field.model + i"
					:componentName="field.component"
					:label="field.label"
					:text="field.entity"
					v-bind="field.props"
					v-model="form[field.model]"
					@blur="updateField(field)"
				/>
			</div>

			<!-- Button -->
			<div class="xs-12 flex xs-between">
				<j-button @click="$emit('done')">Done</j-button>
				<j-button
					:isLoading="isDeleteLoading"
					@click="deleteTransaction"
					type="error"
					>Delete</j-button
				>
			</div>
		</div>

		<!-- Loading -->
		<j-spinner v-else />
	</div>
</template>

<script>
/**
 * This component is responsible for update of case
 * It's a smart component that does API calls
 */
import FormField from "../FormField"
import JSpinner from "@/components/JSpinner"
import JButton from "@/components/JButton"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import { computeSchema } from "../utils/computeSchema.js"

export default {
	components: { FormField, JSpinner, JButton },
	props: {
		claimId: {
			type: String,
			required: true
		},

		transactionId: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			schema: [
				{
					component: "number",
					label: "Amount Billed",
					bindTo: "transaction.amount"
				},
				{
					component: "currency",
					label: "Currency",
					bindTo: "transaction.currencyCode"
				},
				{
					component: "select",
					label: "Status",
					bindTo: "transaction.status",
					props: {
						options: [
							{ name: "Undefined", value: "UNDEFINED" },
							{ name: "Not Started", value: "NOT_STARTED" },
							{ name: "Approved", value: "APPROVED" },
							{ name: "Rejected", value: "REJECTED" },
							{ name: "Timeout", value: "TIMEOUT" },
							{ name: "Cancelled By User", value: "CANCELLED_BY_USER" },
							{ name: "Refunded", value: "REFUNDED" }
						]
					}
				},
				{
					component: "date",
					bindTo: "transaction.dateTime",
					label: "Date / Issue Date"
				},
				{
					component: "date",
					bindTo: "transaction.dueDateTime",
					label: "Due Date"
				},
				{
					component: "date",
					bindTo: "transaction.postDateTime",
					label: "Post Date / Settlement Date"
				},
				{
					component: "longText",
					label: "Remark",
					bindTo: "transaction.facts.remark"
				},
				{
					component: "select",
					label: "Payment Method",
					bindTo: "transaction.method",
					props: {
						options: [
							{ name: "Cash", value: "CASH" },
							{ name: "Credit Card", value: "CREDIT_CARD" },
							{ name: "Giro", value: "GIRO" },
							{ name: "ATM", value: "ATM" },
							{
								name: "Self Service Payment Machine",
								value: "SELF_SERVICE_PAYMENT_MACHINE"
							},
							{ name: "Bank Transfer", value: "BANK_TRANSFER" },
							{ name: "Cheque", value: "CHEQUE" }
						]
					}
				},
				{
					component: "text",
					label: "Bank",
					bindTo: "transaction.facts.bank",
					scalarDataType: "stringValue"
				},
				{
					component: "date",
					label: "Cheque Date",
					bindTo: "transaction.facts.chequeDate",
					scalarDataType: "dateValue"
				},
				{
					component: "text",
					label: "Cheque Number",
					bindTo: "transaction.facts.chequeNumber",
					scalarDataType: "stringValue"
				}
			],

			cache: null,
			form: {},
			isLoading: false,
			isDeleteLoading: false
		}
	},

	computed: {
		/**
		 * Schema with proper component names, added model
		 */
		computedSchema() {
			return computeSchema(this.schema)
		}
	},

	methods: {
		async fetchTransaction() {
			this.form = {}
			this.cache = null

			const query = gql`
				query transaction($where: claimWhereInput) {
					claims(where: $where) {
						list {
							id
							transactions {
								id
								amount
								currencyCode
								dateTime
								dueDateTime
								postDateTime
								method
								status
								facts {
									id
									type
									value {
										booleanValue
										dateValue
										numberValue
										stringValue
									}
								}
							}
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: { where: { id: this.claimId } }
			})
			this.isLoading = false

			// Populate form
			this.cache = res?.data?.claims?.list?.[0]?.transactions?.find(
				item => item.id === this.transactionId
			)
			this.populateFormFromCache()
		},

		/**
		 * This method goes through the cache and populates the form accordingly
		 * based on the schema definition of model
		 */
		populateFormFromCache() {
			const usedInsuredIndex = [] // we collect here indexes of insured that have been already populated in this.form

			this.computedSchema.forEach(field => {
				// Facts
				if (field.bindTo.includes("transaction.facts")) {
					this.$set(
						this.form,
						field.model,
						this.cache?.facts?.find(item => item.type === field.model)?.value?.[
							`${field.dataType || "string"}Value`
						]
					)
					return
				}

				// Rest
				this.$set(this.form, field.model, this.cache?.[field.model])
			})
		},

		async updateField(field) {
			// Facts
			if (field.bindTo.includes("transaction.facts"))
				return await this.upsertFactHandler(field)

			// Root
			await this.transactionHandler(field)
		},

		async upsertFactHandler(field) {
			let cachedData = this?.cache?.facts?.find(
				item => item.type === field.model
			)

			const payload = {
				type: field.model,
				value: {
					[`${field?.dataType || "string"}Value`]: this.form[field.model]
				}
			}

			// Create
			if (!cachedData?.id) {
				const res = await this.apiAddFactToTransaction(
					this.transactionId,
					payload
				)

				// Errors
				if (res?.data?.addFactToTransaction?.status !== "success") {
					this.showError(field.label)
					this.populateFormFromCache()
					return
				}

				// Update cache
				this.cache?.facts?.push({
					id: res?.data?.addFactToTransaction?.createdStatus?.id,
					...payload
				})

				return Promise.resolve()
			}

			// Update
			if (
				cachedData?.value?.[`${field.dataType || "string"}Value`] ===
				this.form[field.model]
			)
				return // check if value is different from cache first

			const res = await this.apiUpdateTransactionFact(
				this.transactionId,
				cachedData?.id,
				payload
			)

			// Errors
			if (res?.data?.updateTransactionFact?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromCache()
				return Promise.resolve()
			}

			// Update cache
			cachedData.value = {
				[`${field?.dataType || "string"}Value`]: this.form[field.model]
			}

			return Promise.resolve()
		},

		async transactionHandler(field) {
			// Check if data actually changed compared to cache
			if (this.form[field.model] === this?.cache?.[field.model]) return

			const res = await this.apiUpdateTransaction(this.transactionId, {
				[field.model]: this.form[field.model] ? this.form[field.model] : null
			})

			if (res?.data?.updateTransaction?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromCache()
				return Promise.resolve()
			}

			// Update cache
			this.cache[field.model] = this.form[field.model]
			return Promise.resolve()
		},

		// API calls
		async apiAddFactToTransaction(transactionId, input) {
			const query = gql`
				mutation addFactToTransaction(
					$transactionId: String!
					$input: addFactInput!
				) {
					addFactToTransaction(transactionId: $transactionId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					transactionId,
					input
				}
			})

			return Promise.resolve(res)
		},

		async apiUpdateTransactionFact(transactionId, factId, input) {
			const query = gql`
				mutation updateTransactionFact(
					$transactionId: String!
					$factId: String!
					$input: updateFactInput!
				) {
					updateTransactionFact(
						transactionId: $transactionId
						factId: $factId
						input: $input
					) {
						status
						errors
					}
				}
			`

			const res = await api({
				query,
				variables: {
					factId,
					transactionId,
					input
				}
			})

			return Promise.resolve(res)
		},

		async apiUpdateTransaction(transactionId, input) {
			const query = gql`
				mutation updateTransaction(
					$transactionId: String!
					$input: updateTransactionInput!
				) {
					updateTransaction(transactionId: $transactionId, input: $input) {
						status
						errors
					}
				}
			`

			const res = await api({
				query,
				variables: {
					transactionId,
					input
				}
			})

			return Promise.resolve(res)
		},

		// Helpers
		showError(fieldName) {
			// Helper function to add error message
			// TODO: localize
			this.$store.dispatch("showError", [
				`Field ${fieldName} wasn't saved, please update it again`
			])
		},

		async deleteTransaction() {
			const query = gql`
				mutation deleteTransaction($transactionId: String!) {
					deleteTransaction(transactionId: $transactionId) {
						status
						errors
					}
				}
			`

			this.isDeleteLoading = true
			const res = await api({
				query,
				variables: { transactionId: this.transactionId }
			})
			this.isDeleteLoading = false

			// Check errors
			if (res?.data?.deleteTransaction?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.deleteTransaction?.errors?.[0] || "There was an error"
				)
				return
			}

			this.$emit("transaction:deleted")
		}
	},

	created() {
		this.fetchTransaction()
	}
}
</script>
